import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  PlusIcon,
  RectangleStackIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { customReportApi } from '../../../api/customReportApi';
import { sessionsApi } from '../../../api/sessionsApi';
import CustomReportLanguages from './CustomReportLanguages';
import CustomReportOutput from './CustomReportOutput';
import {
  apiError,
  collapsedEditorHint,
  digwatchPublishedMessage,
  generationStartedMessage,
  isRunning,
  languageName,
  listOrEmpty,
  OFFICIAL_ID,
  readyDefaultIds,
  SYNTHESIS_MODE,
  transcriptReadyOf,
  versionOptionsOf,
} from '../utils/customReportUtils';
import { useCitationHandler } from '../hooks/useCitationHandler';
import { useIntervalTask } from '../hooks/useIntervalTask';
import { usePromptTemplates } from '../hooks/usePromptTemplates';
import { useReportExports } from '../hooks/useReportExports';
import { useReportForm } from '../hooks/useReportForm';
import { useReportLanguages } from '../hooks/useReportLanguages';
import {
  EditorToggle,
  EmptyPanel,
  GeneratingNote,
  PanelIntro,
  ReportActions,
  SynthesisPublishBar,
  VersionNameField,
} from './CustomReportsParts';
import DigwatchFlagFields from './DigwatchFlagFields';
import MissionReportFields from './MissionReportFields';
import EventSynthesisOutput from './EventSynthesisOutput';
import PromptFields from './PromptFields';
import PublishedLinks from './PublishedLinks';
import SessionSelector from './SessionSelector';
import VersionBar from './VersionBar';
import '../styles/custom-reports.css';

const STATUS_POLL_MS = 3000;

const CustomReportsPanel = ({
  scope = 'session',
  scopeId,
  title = '',
  sessions = [],
  defaultSelectedIds = [],
  officialLabel = 'Official report',
  mode = 'prompt',
  missions = false,
  sessionNumber = null,
  onVersionChange,
  onNotify,
  onCitationClick,
  children,
}) => {
  const hasOfficial = Boolean(children);
  const synthesisPanel = mode === SYNTHESIS_MODE;
  const ofMode = (list) => listOrEmpty(list).filter(
    (page) => (page.mode === SYNTHESIS_MODE) === synthesisPanel
  );
  const [pages, setPages] = useState([]);
  const [selectedId, setSelectedId] = useState(hasOfficial ? OFFICIAL_ID : '');
  const [availableSessions, setAvailableSessions] = useState(sessions);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [publishingDigwatch, setPublishingDigwatch] = useState(false);
  const [publishingMissions, setPublishingMissions] = useState(false);
  const [settingOfficial, setSettingOfficial] = useState(false);
  const [output, setOutput] = useState(null);
  const [editorOpen, setEditorOpen] = useState(true);
  const templates = usePromptTemplates();
  const statusPoll = useIntervalTask();
  const titleRef = useRef(null);
  const selectedIdRef = useRef(OFFICIAL_ID);

  selectedIdRef.current = selectedId;
  const selectedPage = pages.find((page) => page.id === selectedId) || null;
  const isOfficial = hasOfficial && selectedId === OFFICIAL_ID;
  const running = isRunning(selectedPage);
  const completed = selectedPage?.status === 'COMPLETED' && (selectedPage?.has_output || !!output);
  const isSynthesis = selectedPage?.mode === SYNTHESIS_MODE;

  const form = useReportForm({ scope, scopeId, isSynthesis, defaultSelectedIds, missions });
  const { reportTitle, promptText, selectedIds, currentSessionIds } = form;
  const langs = useReportLanguages({
    scope, scopeId, selectedId, selectedIdRef, completed, setOutput, setError, onNotify,
  });
  const { languages, activeLanguage, translationLang } = langs;
  const reportExports = useReportExports({
    scope, scopeId, selectedId, isOfficial, reportTitle, activeLanguage, translationLang, setError, onNotify,
  });

  const transcriptReferences = output?.transcript_references || null;
  const handleCitation = useCitationHandler(transcriptReferences, onCitationClick);

  const readySessions = useMemo(
    () => availableSessions.filter(transcriptReadyOf),
    [availableSessions]
  );

  const versionOptions = useMemo(
    () => versionOptionsOf(pages, hasOfficial, officialLabel),
    [hasOfficial, officialLabel, pages]
  );

  const applyPage = (page, fallbackIds, { keepEditor = false } = {}) => {
    setSelectedId(page.id);
    onVersionChange?.(page.id);
    form.fillFrom(page, fallbackIds);
    setOutput(page.output || null);
    reportExports.setNewsletters(page.newsletters || {});
    langs.applyLanguages(page);
    // A finished report opens on its text; a draft opens on its prompt.
    // Saving a flag must not slam the editor shut or the DigWatch select vanishes.
    if (!keepEditor) setEditorOpen(!page.output);
  };

  const clearSelection = () => {
    statusPoll.stop();
    langs.resetLanguages();
    const nextId = hasOfficial ? OFFICIAL_ID : '';
    setSelectedId(nextId);
    onVersionChange?.(nextId);
    form.clear();
    setError('');
    setOutput(null);
    reportExports.setNewsletters({});
    setEditorOpen(true);
  };

  // Runs when leaving a version. A silent failure here loses the prompt the
  // user just typed, so it reports even though nobody asked for a save.
  const persistCurrentDraft = async () => {
    if (isOfficial || !selectedId || running) return;
    try {
      await customReportApi.update(scope, scopeId, selectedId, form.payload());
    } catch (err) {
      setError(apiError(err, 'Your last changes could not be saved'));
    }
  };

  const mergePage = (full) => {
    setPages((prev) => prev.map((page) => (page.id === full.id ? { ...page, ...full } : page)));
  };

  useEffect(() => {
    let cancelled = false;

    const hydrate = async () => {
      if (!scopeId) return;
      setLoading(true);
      try {
        const loaded = scope === 'event' && (!sessions || sessions.length === 0)
          ? await sessionsApi.listAllByEvent(scopeId)
          : sessions;
        if (!cancelled) setAvailableSessions(loaded);

        const list = ofMode(await customReportApi.list(scope, scopeId));
        if (cancelled) return;
        setPages(list);
        if (!hasOfficial && !selectedIdRef.current && list.length > 0) {
          await openPage(list[0].id);
        }
      } catch (err) {
        if (!cancelled) setError(apiError(err, 'Failed to load report versions'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    hydrate();
    return () => {
      cancelled = true;
      statusPoll.stop();
      langs.stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, scopeId]);

  const startPolling = () => {
    statusPoll.start(async () => {
      try {
        const currentId = selectedIdRef.current;
        if (!currentId || currentId === OFFICIAL_ID) return;
        const full = await customReportApi.get(scope, scopeId, currentId);
        if (currentId !== selectedIdRef.current) return;
        mergePage(full);
        setOutput(full.output || null);
        langs.applyLanguages(full);
        if (!isRunning(full)) {
          statusPoll.stop();
          setSubmitting(false);
          if (full.status === 'FAILED') {
            setError(full.error_message || 'Generation failed');
          } else if (full.output) {
            setEditorOpen(false);
          }
        }
      } catch (err) {
        statusPoll.stop();
        setSubmitting(false);
        setError(apiError(err, 'Failed to check status'));
      }
    }, STATUS_POLL_MS);
  };

  const openPage = async (pageId) => {
    const full = await customReportApi.get(scope, scopeId, pageId);
    const fallback = readyDefaultIds(defaultSelectedIds, readySessions);
    applyPage(full, fallback.length ? fallback : selectedIds);
    mergePage(full);
    if (isRunning(full)) startPolling();
    else statusPoll.stop();
    langs.resumeTranslation(full.languages);
  };

  const handleSelect = async (nextId) => {
    setError('');
    if (!nextId || nextId === OFFICIAL_ID) {
      await persistCurrentDraft();
      clearSelection();
      return;
    }
    try {
      await persistCurrentDraft();
      await openPage(nextId);
    } catch (err) {
      setError(apiError(err, 'Failed to open report version'));
    }
  };

  const handleAdd = async () => {
    setError('');
    try {
      await persistCurrentDraft();
      const fallback = readyDefaultIds(defaultSelectedIds, readySessions);
      const created = await customReportApi.create(scope, scopeId, {
        title: `Custom report ${pages.length + 1}`,
        session_ids: fallback.length
          ? fallback
          : (scope === 'session' && scopeId ? [scopeId] : []),
        ...(scope === 'event' ? { report_kind: '', take_official: false } : {}),
      });
      setPages((prev) => [created, ...prev.filter((page) => page.id !== created.id)]);
      applyPage(created, created.session_ids || []);
      setTimeout(() => titleRef.current?.focus(), 50);
    } catch (err) {
      setError(apiError(err, 'Failed to add report version'));
    }
  };

  const handleAddSynthesis = async () => {
    setError('');
    try {
      await persistCurrentDraft();
      const count = pages.filter((page) => page.mode === SYNTHESIS_MODE).length;
      const created = await customReportApi.create(scope, scopeId, {
        title: `In-depth report ${count + 1}`,
        session_ids: [],
        mode: SYNTHESIS_MODE,
      });
      setPages((prev) => [created, ...prev.filter((page) => page.id !== created.id)]);
      applyPage(created, []);
    } catch (err) {
      setError(apiError(err, 'Failed to add in-depth report'));
    }
  };

  const handleDelete = async () => {
    if (isOfficial || !selectedId || running) return;
    if (!window.confirm(`Delete “${reportTitle || 'this version'}”?`)) return;
    try {
      const deletedId = selectedId;
      await customReportApi.remove(scope, scopeId, deletedId);
      const remaining = pages.filter((page) => page.id !== deletedId);
      setPages(remaining);
      if (!hasOfficial && remaining.length > 0) {
        statusPoll.stop();
        await openPage(remaining[0].id);
      } else {
        clearSelection();
      }
    } catch (err) {
      setError(apiError(err, 'Failed to delete'));
    }
  };

  const handleMakeOfficial = async () => {
    if (isOfficial || !selectedId) return;
    setSettingOfficial(true);
    setError('');
    try {
      const saved = await customReportApi.setOfficial(scope, scopeId, selectedId);
      applyPage(saved, saved.session_ids || selectedIds);
      setPages(ofMode(await customReportApi.list(scope, scopeId)));
      onNotify?.({ type: 'success', title: 'Official flag updated', message: 'Previous official report was kept.' });
    } catch (err) {
      setError(apiError(err, 'Failed to move official flag'));
    } finally {
      setSettingOfficial(false);
    }
  };

  const handlePublishDigwatch = async () => {
    if (isOfficial || !selectedId || scope !== 'event') return;
    if (isSynthesis && !window.confirm(
      selectedPage?.digwatch?.post_id
        ? 'Update the in-depth report on DigWatch with this version?'
        : 'Publish this version as the event\'s official in-depth report on DigWatch?'
    )) return;
    setPublishingDigwatch(true);
    setError('');
    try {
      if (!isSynthesis) await persistDraft();
      const result = await customReportApi.publishToDigwatch(scope, scopeId, selectedId, isSynthesis ? undefined : activeLanguage);
      const refreshed = await customReportApi.get(scope, scopeId, selectedId);
      applyPage(refreshed, refreshed.session_ids || selectedIds);
      mergePage(refreshed);
      onNotify?.({
        type: 'success',
        title: 'Published to DigWatch',
        message: digwatchPublishedMessage(isSynthesis, result),
      });
    } catch (err) {
      setError(apiError(err, 'Failed to publish this report to DigWatch'));
    } finally {
      setPublishingDigwatch(false);
    }
  };

  const handlePublishMissions = async () => {
    if (isOfficial || !selectedId || isSynthesis) return;
    setPublishingMissions(true);
    setError('');
    try {
      await persistDraft();
      await customReportApi.sendToMissions(scope, scopeId, selectedId, activeLanguage);
      const refreshed = await customReportApi.get(scope, scopeId, selectedId);
      applyPage(refreshed, refreshed.session_ids || selectedIds);
      mergePage(refreshed);
      onNotify?.({ type: 'success', title: 'Sent to missions', message: 'The report was sent as markdown.' });
    } catch (err) {
      setError(apiError(err, 'Failed to send this report to missions'));
    } finally {
      setPublishingMissions(false);
    }
  };

  const persistDraft = async ({ notify = false, payload } = {}) => {
    if (isOfficial || !selectedId || running) return;
    if (notify) {
      setSaving(true);
      setError('');
    }
    try {
      const saved = await customReportApi.update(scope, scopeId, selectedId, payload || form.payload());
      applyPage(saved, saved.session_ids || selectedIds, { keepEditor: true });
      mergePage(saved);
      if (notify) onNotify?.({ type: 'success', title: 'Saved', message: 'Report version updated.' });
    } catch (err) {
      setError(apiError(err, 'Failed to save'));
    } finally {
      if (notify) setSaving(false);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (isOfficial || !selectedId) return;
    const text = promptText.trim();
    if (!reportTitle.trim()) {
      setError('Name this report version first.');
      return;
    }
    if (!text && !isSynthesis) {
      setError('Paste the prompt first.');
      return;
    }
    if (currentSessionIds.length === 0 && !isSynthesis) {
      setError('Select at least one session with a completed transcript.');
      return;
    }
    // Translations belong to the output being replaced, so the backend drops
    // them on regenerate. Say so rather than letting them disappear.
    const existing = languages.filter((item) => !item.is_original);
    if (existing.length > 0) {
      const names = existing.map((item) => languageName(item.code)).join(', ');
      if (!window.confirm(`Regenerating removes the ${names} translation. Continue?`)) return;
    }
    setError('');
    setSubmitting(true);
    try {
      const started = await customReportApi.generate(scope, scopeId, selectedId, form.payload());
      mergePage(started);
      langs.applyLanguages(started);
      onNotify?.({
        type: 'info',
        title: isSynthesis ? 'In-depth report started' : 'Custom report started',
        message: generationStartedMessage(isSynthesis, started, currentSessionIds.length),
      });
      startPolling();
    } catch (err) {
      setSubmitting(false);
      setError(apiError(err, 'Failed to start'));
    }
  };

  /**
   * Store a hand edit of the text currently on screen.
   *
   * The edit belongs to the language being read, so the English and the French
   * version each keep their own wording. Errors are raised back to the editor,
   * which keeps the typed text open rather than losing it.
   */
  const handleSaveContent = async ({ content, sessionIndex }) => {
    if (!selectedId) return;
    setError('');
    const full = await customReportApi.updateContent(scope, scopeId, selectedId, {
      content,
      sessionIndex,
      lang: translationLang,
    });
    mergePage(full);
    setOutput(full.output || null);
    langs.applyLanguages(full);
    onNotify?.({
      type: 'success',
      title: 'Report text saved',
      message: translationLang
        ? `Your ${languageName(translationLang)} wording is stored.`
        : 'Your wording is stored. Existing translations are marked out of date.',
    });
  };

  const renameSelectedVersion = (value) => {
    form.setReportTitle(value);
    setPages((prev) => prev.map((page) => (
      page.id === selectedId ? { ...page, title: value || 'Untitled report' } : page
    )));
  };

  const changeFlag = (change) => {
    form.changeFlags(change);
    persistDraft({ payload: form.payload(change) });
  };

  return (
    <div className="custom-reports-wrap">
      {versionOptions.length > 0 && (
      <VersionBar scopeId={scopeId} options={versionOptions} selectedId={selectedId} disabled={running} onSelect={handleSelect}>
        {synthesisPanel ? (
          <button
            type="button"
            onClick={handleAddSynthesis}
            disabled={running || loading}
            title="Merge every session report of this event into one report with speakers, agreements and differences"
          >
            <RectangleStackIcon /> New in-depth report
          </button>
        ) : (
          <button type="button" onClick={handleAdd} disabled={running || loading}>
            <PlusIcon /> New version
          </button>
        )}
        {!isOfficial && selectedId && (
          <button type="button" onClick={handleDelete} disabled={running}>
            <TrashIcon /> Delete
          </button>
        )}
      </VersionBar>
      )}

      {isOfficial ? children : !selectedId ? (
        <EmptyPanel
          synthesis={synthesisPanel}
          scope={scope}
          loading={loading}
          onAdd={synthesisPanel ? handleAddSynthesis : handleAdd}
        />
      ) : (
        <form onSubmit={handleGenerate} className="custom-reports-editor-card custom-reports-form">
          {missions && !isSynthesis && (
            <MissionReportFields
              scopeId={scopeId}
              country={form.country}
              sessionNumber={scope === 'session' ? sessionNumber : null}
              disabled={running}
              onChange={changeFlag}
            />
          )}

          {scope === 'event' && !isSynthesis && (
            <DigwatchFlagFields
              scopeId={scopeId}
              pages={pages}
              selectedId={selectedId}
              reportKind={form.reportKind}
              dayNumber={form.dayNumber}
              takeOfficial={form.takeOfficial}
              disabled={running}
              onChange={changeFlag}
            />
          )}

          <EditorToggle
            open={editorOpen}
            onToggle={() => setEditorOpen((prev) => !prev)}
            title={isSynthesis ? 'Name & sessions' : 'Name & prompt'}
            collapsedHint={collapsedEditorHint(reportTitle, scope, currentSessionIds.length)}
          />

          <div className={`custom-reports-collapsible${editorOpen ? '' : ' closed'}`}>
          <PanelIntro title={title || (scope === 'event' ? 'Event' : 'Session')} synthesis={isSynthesis} officialLabel={officialLabel} />

          <VersionNameField
            ref={titleRef}
            scopeId={scopeId}
            value={reportTitle}
            onChange={renameSelectedVersion}
            onBlur={() => persistDraft()}
            disabled={running}
          />

          {!isSynthesis && (
            <PromptFields
              key={selectedId}
              scope={scope}
              scopeId={scopeId}
              templates={templates}
              prompt={promptText}
              onPromptChange={form.setPromptText}
              disabled={running}
            />
          )}

          {scope === 'event' && (
            <SessionSelector sessions={readySessions} selectedIds={selectedIds} onChange={form.setSelectedIds} disabled={running} />
          )}
          </div>

          {(running || submitting) && <GeneratingNote page={selectedPage} />}
          {error && <div className="custom-reports-error">{error}</div>}

          <ReportActions
            showOutputActions={completed && !running}
            eventPage={scope === 'event' && !isSynthesis}
            canMakeOfficial={Boolean(selectedPage?.report_kind && !selectedPage.is_official)}
            running={running}
            saving={saving}
            downloading={reportExports.downloading}
            settingOfficial={settingOfficial}
            publishingDigwatch={publishingDigwatch}
            publishingMissions={publishingMissions}
            sendingNewsletter={reportExports.sendingNewsletter}
            generating={running || submitting}
            canGenerate={!(currentSessionIds.length === 0 && !isSynthesis)}
            generateLabel={isSynthesis ? 'Generate in-depth report' : 'Generate JSON'}
            languageLabel={languageName(activeLanguage)}
            onSave={() => persistDraft({ notify: true })}
            onDownload={reportExports.download}
            onMakeOfficial={handleMakeOfficial}
            onPublishDigwatch={handlePublishDigwatch}
            onPublishMissions={handlePublishMissions}
            showMissions={missions && !isSynthesis}
            onCreateNewsletter={reportExports.createNewsletter}
          />

          {isSynthesis && completed && !running && scope === 'event' && (
            <SynthesisPublishBar
              publishing={publishingDigwatch}
              digwatch={selectedPage?.digwatch}
              onPublish={handlePublishDigwatch}
            />
          )}

          <PublishedLinks
            digwatchUrl={selectedPage?.digwatch?.post_url}
            newsletters={reportExports.newsletters}
            missions={selectedPage?.missions}
          />

          {completed && !running && !isSynthesis && (
            <CustomReportLanguages
              languages={languages}
              missing={langs.missingLanguages}
              activeLanguage={activeLanguage}
              busy={langs.translating}
              onSelect={langs.selectLanguage}
              onTranslate={langs.translate}
              onDelete={langs.deleteTranslation}
            />
          )}

          {isSynthesis ? (
            <EventSynthesisOutput output={output} onCitationClick={handleCitation} />
          ) : (
          <CustomReportOutput
            output={output}
            title={reportTitle}
            languageLabel={translationLang ? languageName(translationLang) : ''}
            editable={completed && !running && !langs.translating}
            onSave={handleSaveContent}
            transcriptReferences={transcriptReferences}
            onCitationClick={handleCitation}
          />
          )}
        </form>
      )}
    </div>
  );
};

export default CustomReportsPanel;
